import { supabase } from '../lib/supabase';

export const notificationService = {
  // Enhanced notification fetching with category filtering
  async getNotifications(userId, options = {}) {
    let query = supabase
      ?.from('notifications')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.order('created_at', { ascending: false });

    // Apply category filter if provided
    if (options?.category) {
      query = query?.eq('category', options?.category);
    }

    // Apply read status filter if provided
    if (options?.isRead !== undefined) {
      query = query?.eq('is_read', options?.isRead);
    }

    // Apply limit
    query = query?.limit(options?.limit || 50);

    const { data, error } = await query;
    
    if (error) throw error;
    
    return data?.map(notif => ({
      id: notif?.id,
      userId: notif?.user_id,
      title: notif?.title,
      message: notif?.message,
      type: notif?.type,
      category: notif?.category,
      status: notif?.status,
      isRead: notif?.is_read,
      readAt: notif?.read_at,
      metadata: notif?.metadata,
      actionUrl: notif?.action_url,
      priority: notif?.priority,
      sentAt: notif?.sent_at,
      createdAt: notif?.created_at
    }));
  },

  // Get unread count by category
  async getUnreadCount(userId, category = null, signal = null) {
    let query = supabase
      ?.from('notifications')
      ?.select('id', { count: 'exact', head: true })
      ?.eq('user_id', userId)
      ?.eq('is_read', false);

    if (category) {
      query = query?.eq('category', category);
    }

    const { count, error } = await query;
    
    if (error) {
      // Don't throw if it's an abort error
      if (error?.name === 'AbortError') {
        return 0;
      }
      throw error;
    }
    return count || 0;
  },

  // Mark single notification as read
  async markAsRead(notificationId) {
    const { data, error } = await supabase
      ?.from('notifications')
      ?.update({ is_read: true })
      ?.eq('id', notificationId)
      ?.select()
      ?.single();
    
    if (error) throw error;
    return data;
  },

  // Mark all notifications as read for a user
  async markAllAsRead(userId, category = null) {
    let query = supabase
      ?.from('notifications')
      ?.update({ is_read: true })
      ?.eq('user_id', userId)
      ?.eq('is_read', false);

    if (category) {
      query = query?.eq('category', category);
    }

    const { data, error } = await query?.select();
    
    if (error) throw error;
    return data;
  },

  // Delete notification
  async deleteNotification(notificationId) {
    const { error } = await supabase
      ?.from('notifications')
      ?.delete()
      ?.eq('id', notificationId);
    
    if (error) throw error;
  },

  // Create in-app notification with category
  async createInAppNotification({
    userId,
    title,
    message,
    category,
    metadata = {},
    actionUrl = null,
    priority = 'normal'
  }) {
    const { data, error } = await supabase
      ?.from('notifications')
      ?.insert({
        user_id: userId,
        title,
        message,
        type: 'in_app',
        category,
        metadata,
        action_url: actionUrl,
        priority,
        status: 'sent',
        sent_at: new Date()?.toISOString(),
        is_read: false
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    return data;
  },

  // Create session notification
  async notifyLiveSession({
    userId,
    sessionId,
    instituteName,
    sessionType,
    scheduledAt
  }) {
    return await this.createInAppNotification({
      userId,
      title: 'Live Session Starting Soon',
      message: `${instituteName} has a ${sessionType} session starting at ${new Date(scheduledAt)?.toLocaleTimeString()}`,
      category: 'session',
      metadata: { sessionId, instituteName, sessionType },
      actionUrl: `/video-counseling?session=${sessionId}`,
      priority: 'high'
    });
  },

  // Create chat notification
  async notifyChatMessage({
    userId,
    conversationId,
    senderName,
    messagePreview
  }) {
    return await this.createInAppNotification({
      userId,
      title: `New message from ${senderName}`,
      message: messagePreview,
      category: 'chat',
      metadata: { conversationId, senderName },
      actionUrl: `/chat-interface?conversation=${conversationId}`,
      priority: 'normal'
    });
  },

  // Create event reminder notification
  async notifyEventReminder({
    userId,
    eventId,
    eventTitle,
    eventDate
  }) {
    return await this.createInAppNotification({
      userId,
      title: 'Event Reminder',
      message: `${eventTitle} is scheduled for ${new Date(eventDate)?.toLocaleDateString()}`,
      category: 'event',
      metadata: { eventId, eventTitle },
      actionUrl: `/exhibition-hall?event=${eventId}`,
      priority: 'normal'
    });
  },

  // Create institute response notification
  async notifyInstituteResponse({
    userId,
    instituteName,
    responseType,
    message
  }) {
    return await this.createInAppNotification({
      userId,
      title: `Response from ${instituteName}`,
      message,
      category: 'institute_response',
      metadata: { instituteName, responseType },
      actionUrl: '/student-dashboard',
      priority: 'normal'
    });
  },

  async createNotification(notificationData) {
    const { data, error } = await supabase?.from('notifications')?.insert({
        user_id: notificationData?.userId,
        title: notificationData?.title,
        message: notificationData?.message,
        type: notificationData?.type || 'in_app',
        status: 'pending'
      })?.select()?.single();
    
    if (error) throw error;
    return data;
  },

  async markAsSent(notificationId) {
    const { data, error } = await supabase?.from('notifications')?.update({ status: 'sent', sent_at: new Date()?.toISOString() })?.eq('id', notificationId)?.select()?.single();
    
    if (error) throw error;
    return data;
  },

  async sendEmail({ to, subject, html, type = 'general' }) {
    try {
      console.log('🔵 [Notification Service] Invoking send-email Edge Function');
      console.log('🔵 [Notification Service] Email params:', { to, subject, type });
      
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL?.replace(/\/$/, '');
      const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;
      const edgeFunctionUrl = `${supabaseUrl}/functions/v1/send-email`;

      // Try direct fetch first (more reliable than supabase.functions.invoke)
      let lastError = null;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          console.log(`🔵 [Notification Service] Attempt ${attempt}/3 - calling Edge Function directly`);
          const response = await fetch(edgeFunctionUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${supabaseAnonKey}`,
              'apikey': supabaseAnonKey
            },
            body: JSON.stringify({ to, subject, html, type })
          });

          if (!response?.ok) {
            const errData = await response?.json()?.catch(() => ({}));
            const errMsg = errData?.error || `Edge Function returned status ${response?.status}`;
            console.error(`❌ [Notification Service] Attempt ${attempt} failed:`, errMsg);
            lastError = new Error(errMsg);
            if (response?.status >= 400 && response?.status < 500) {
              // Client error - no point retrying
              break;
            }
            if (attempt < 3) {
              await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
            }
            continue;
          }

          const data = await response?.json();
          console.log('✅ [Notification Service] Email sent successfully:', data);
          return data;
        } catch (fetchErr) {
          console.error(`❌ [Notification Service] Attempt ${attempt} fetch error:`, fetchErr?.message);
          lastError = fetchErr;
          if (attempt < 3) {
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
          }
        }
      }

      // All attempts failed - try supabase.functions.invoke as last resort
      console.log('🔄 [Notification Service] Trying supabase.functions.invoke as fallback...');
      try {
        const { data, error } = await supabase?.functions?.invoke('send-email', {
          body: { to, subject, html, type }
        });
        if (!error) {
          console.log('✅ [Notification Service] Email sent via supabase.functions.invoke:', data);
          return data;
        }
        console.error('❌ [Notification Service] supabase.functions.invoke also failed:', error);
      } catch (invokeErr) {
        console.error('❌ [Notification Service] supabase.functions.invoke threw:', invokeErr?.message);
      }

      // Throw a user-friendly error (not "network connection failed")
      throw new Error('Email service is temporarily unavailable. Please try again in a moment.');
    } catch (error) {
      console.error('❌ [Notification Service] Error sending email:', error);
      // Rethrow with a user-friendly message if it's a fetch/network error
      if (error?.message?.includes('Failed to fetch') || error?.message?.includes('NetworkError') || error?.message?.includes('Failed to send a request')) {
        throw new Error('Email service is temporarily unavailable. Please try again in a moment.');
      }
      throw error;
    }
  },

  async sendSMS({ to, message, type = 'general' }) {
    try {
      console.log('🔵 [Notification Service] Invoking send-sms Edge Function');
      console.log('🔵 [Notification Service] SMS params:', { to, type });
      
      const { data, error } = await supabase?.functions?.invoke('send-sms', {
        body: { to, message, type }
      });

      if (error) {
        console.error('❌ [Notification Service] Edge Function error:', error);
        console.error('❌ [Notification Service] Error details:', {
          message: error?.message,
          status: error?.status,
          context: error?.context
        });
        throw error;
      }
      
      console.log('✅ [Notification Service] SMS Edge Function response:', data);
      return data;
    } catch (error) {
      console.error('❌ [Notification Service] Error sending SMS:', error);
      throw error;
    }
  },

  async sendSessionReminder(session, userEmail, userPhone) {
    const scheduledDate = new Date(session?.scheduledAt);
    const formattedDate = scheduledDate?.toLocaleDateString('en-US', { 
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
    });
    const formattedTime = scheduledDate?.toLocaleTimeString('en-US', { 
      hour: '2-digit', minute: '2-digit' 
    });

    // Send email reminder
    if (userEmail) {
      const emailHtml = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #2563eb;">Session Reminder</h2>
          <p>Hi there,</p>
          <p>This is a reminder that you have an upcoming counseling session scheduled:</p>
          <div style="background-color: #f3f4f6; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <p><strong>Date:</strong> ${formattedDate}</p>
            <p><strong>Time:</strong> ${formattedTime}</p>
            <p><strong>Duration:</strong> ${session?.durationMinutes} minutes</p>
            <p><strong>Type:</strong> ${session?.sessionType || 'Counseling'}</p>
          </div>
          ${session?.meetingUrl ? `<p><a href="${session?.meetingUrl}" style="background-color: #2563eb; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; display: inline-block;">Join Session</a></p>` : ''}
          <p>Please be on time. Looking forward to connecting with you!</p>
        </div>
      `;

      await this.sendEmail({
        to: userEmail,
        subject: `Session Reminder - ${formattedDate} at ${formattedTime}`,
        html: emailHtml,
        type: 'session_reminder'
      });
    }

    // Send SMS reminder
    if (userPhone) {
      const smsMessage = `Session Reminder: You have a counseling session on ${formattedDate} at ${formattedTime}. Duration: ${session?.durationMinutes} min. ${session?.meetingUrl ? `Join: ${session?.meetingUrl}` : ''}`;
      
      await this.sendSMS({
        to: userPhone,
        message: smsMessage,
        type: 'session_reminder'
      });
    }
  },

  async sendLeadFollowUp(lead, instituteEmail, institutePhone) {
    const studentName = lead?.student?.name || 'Student';
    const followUpDate = new Date(lead?.followUpDate)?.toLocaleDateString('en-US', { 
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
    });

    // Send email follow-up
    if (instituteEmail) {
      const emailHtml = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #2563eb;">Lead Follow-Up Reminder</h2>
          <p>Hi there,</p>
          <p>This is a reminder to follow up with your lead:</p>
          <div style="background-color: #f3f4f6; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <p><strong>Student:</strong> ${studentName}</p>
            <p><strong>Email:</strong> ${lead?.student?.email || 'N/A'}</p>
            <p><strong>Phone:</strong> ${lead?.student?.phone || 'N/A'}</p>
            <p><strong>Status:</strong> ${lead?.leadStatus}</p>
            <p><strong>Follow-up Date:</strong> ${followUpDate}</p>
            ${lead?.notes ? `<p><strong>Notes:</strong> ${lead?.notes}</p>` : ''}
          </div>
          <p>Don't miss this opportunity to connect with a potential student!</p>
        </div>
      `;

      await this.sendEmail({
        to: instituteEmail,
        subject: `Lead Follow-Up: ${studentName}`,
        html: emailHtml,
        type: 'lead_followup'
      });
    }

    // Send SMS follow-up
    if (institutePhone) {
      const smsMessage = `Lead Follow-Up: Time to connect with ${studentName} (${lead?.leadStatus}). Contact: ${lead?.student?.email || lead?.student?.phone || 'See dashboard'}`;
      
      await this.sendSMS({
        to: institutePhone,
        message: smsMessage,
        type: 'lead_followup'
      });
    }
  },

  async sendChatNotification(recipientEmail, recipientPhone, senderName, messagePreview) {
    // Send email notification
    if (recipientEmail) {
      const emailHtml = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #2563eb;">New Message</h2>
          <p>You have a new message from <strong>${senderName}</strong>:</p>
          <div style="background-color: #f3f4f6; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <p style="font-style: italic;">${messagePreview}</p>
          </div>
          <p>Log in to your dashboard to view and respond to this message.</p>
        </div>
      `;

      await this.sendEmail({
        to: recipientEmail,
        subject: `New message from ${senderName}`,
        html: emailHtml,
        type: 'chat_notification'
      });
    }

    // Send SMS notification
    if (recipientPhone) {
      const smsMessage = `New message from ${senderName}: "${messagePreview?.substring(0, 100)}${messagePreview?.length > 100 ? '...' : ''}" - Check your dashboard to reply.`;
      
      await this.sendSMS({
        to: recipientPhone,
        message: smsMessage,
        type: 'chat_notification'
      });
    }
  },

  async sendBoothUpdate(studentEmail, studentPhone, instituteName, updateMessage) {
    // Send email notification
    if (studentEmail) {
      const emailHtml = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #2563eb;">Booth Update from ${instituteName}</h2>
          <p>There's a new update from <strong>${instituteName}</strong>:</p>
          <div style="background-color: #f3f4f6; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <p>${updateMessage}</p>
          </div>
          <p>Visit their virtual booth to learn more!</p>
        </div>
      `;

      await this.sendEmail({
        to: studentEmail,
        subject: `Update from ${instituteName}`,
        html: emailHtml,
        type: 'booth_update'
      });
    }

    // Send SMS notification
    if (studentPhone) {
      const smsMessage = `${instituteName} has a new update: ${updateMessage?.substring(0, 120)}${updateMessage?.length > 120 ? '...' : ''} - Visit their booth to learn more!`;
      
      await this.sendSMS({
        to: studentPhone,
        message: smsMessage,
        type: 'booth_update'
      });
    }
  }
};